import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';

/**
 * @description Checks if item is of JSON (Object?) type
 * @see {@link http://stackoverflow.com/questions/9804777/how-to-test-if-a-string-is-json-or-not}
 * @param value {Object}
 * @return {Boolean}
 */
export const isJSON = ( value: any ): boolean => {
	value = typeof value !== TrueType.STRING
		? JSON.stringify( value )
		: value;

	try {
		value = JSON.parse( value );
	} catch ( error ) {
		return false;
	}

	return ( typeof value === TrueType.OBJECT && value !== null );
};

/**
 * @description Returns true type name as string, Should be used with TrueType.enum
 * @param value
 * @returns {TrueType}
 * @example
 * trueType( 10 ) === TrueType.NUMBER // output => true
 */
export const trueType = ( value: any ): TrueType => {
	return Object.prototype.toString.call( value ).slice( 8, -1 ).toLowerCase();
};

/**
 * @description Checks if object is empty
 * @param value {Object|Boolean|String|Number|Array}
 * @return {Boolean}
 */
export const isEmpty = ( value: any ): boolean => {
	if ( value === null || value === undefined ) return true; // null and undefined are "empty"
	if ( isBoolean( value ) ) return false; // defined Boolean is not "empty"
	if ( isNumber( value ) ) return false; // defined Number is not "empty"
	if ( isArray( value ) ) return ( value.length === 0 ); // empty Array is "empty"
	if ( isString( value ) ) return ( value.trim().length === 0 ); // empty String is "empty"
	if ( isObject( value ) ) return ( Object.keys( value ).length === 0 ); // object without properties
	return true;
};

/**
 * @description Checks if string is empty
 * <p>RETURN: = 'true' if 'str' is empty string, null, undefined, or consists of white-spaces only</p>
 * @param value {String}
 * @return {Boolean}
 */
export const isWhitespaceEmptyString = ( value: string ): boolean => {
	return value ? !( /\S/.test( value ) ) : ( value === '' || value === null || value === undefined );
};

/**
 * @description Checks if var is a function
 * @param value {Object}
 * @return {Boolean} True if `value` is a `Function`.
 */
export const isFunction = ( value: any ): boolean => {
	const getType = {};
	if( !value ) {
		return false;
	}
	const valueType = getType.toString.call( value );
	return valueType === '[object Function]' || valueType === '[object AsyncFunction]';
};

/**
 * @description Checks if var is a Boolean
 * @param value {Object}
 * @return {Boolean}
 */
export const isBoolean = ( value: any ): boolean => {
	return typeof value === TrueType.BOOLEAN;
};

/**
 * @description Checks if var is a Number
 * @param value {Object}
 * @return {Boolean} True if `value` is a `Number`.
 */
export const isNumber = ( value: any ): boolean => {
	if ( isNaN( value ) ) return false; // if arg is NaN -> return false
	return ( typeof value === TrueType.NUMBER && isFinite( value ) );
};

/**
 * @description Integer
 * @param value {*}
 * @return {*|boolean}
 */
export const isInteger = ( value: any ): boolean => {
	return typeof value === TrueType.NUMBER && isFinite( value ) && Number.isInteger( value );
};

/**
 * @description Checks if number is decimal
 * @param value {Number}
 * @return {Boolean} True if number is decimal.
 */
export const isDecimal = ( value: number = 0 ): boolean => {
	if ( !isNumber( value ) ) return false;
	return ( value ^ 0 ) !== value;
};

/**
 * @description Checks if var is a String
 * @param value {Object}
 * @return {Boolean} True if `value` is a `String`.
 */
export const isString = ( value: any ): boolean => {
	return ( typeof value === TrueType.STRING || value instanceof String );
};

/**
 * @description Checks if var is an empty String, or if string contains spaces only
 * @param value {String}
 * @return {Boolean} // returns true if string contains 0+ spaces only
 */
export const isEmptyString = ( value: string ): boolean => {
	return /^ *$/.test( value ); // returns true string contains 0+ spaces only
};

/**
 * @description Determine if a value is an object with a null prototype
 * @param value {Object}
 * @return {Boolean} True if `value` is an `Object` with a null prototype
 */
export const isBlankObject = ( value: any ): boolean => {
	return value !== null && typeof value === TrueType.OBJECT && !Object.getPrototypeOf( value );
};

/**
 * @description Checks if var is an Object
 * @param value {Object}
 * @return {Boolean} True if `value` is an `Object` but not `null`.
 */
export const isObject = ( value: any ): boolean => {
	if ( isUndefined( value ) === true ) return false; // undefined use case
	// return ( value && typeof value === TrueType.OBJECT && value.constructor === Object );
	return ( value && typeof value === TrueType.OBJECT );
};

/**
 * @description Checks if var is Array
 * @param value {Object}
 * @return {Boolean}
 */
export const isArray = ( value: any ): boolean => {
	return Array.isArray( value );
};

/**
 * @description Checks if object is iterable
 * @param obj
 * @returns {boolean}
 */
export const isIterable = ( obj: any ): boolean => {
	// checks for null and undefined
	if ( obj === null || obj === undefined ) return false;
	// checks for iterator method
	return typeof obj[ Symbol?.iterator ] === TrueType.FUNCTION;
};

/**
 * @description Null, undefined, empty, or an error
 * @param value {*}
 * @return {*|boolean}
 */
export const isBad = ( value: any ): boolean => {
	return isNull( value ) || isUndefined( value ) || isEmpty( value ) || isError( value );
};

/**
 * @description Determines if a reference is a DOM element
 * @param node {any}
 * @return {Boolean}
 */
export const isElement = ( node: any | HTMLElement ): boolean => {
	return ( node !== undefined && node !== null && isFunction( node?.appendChild ) );
};

/**
 * @description Determines if a reference is a wrapped jQuery element
 * @param obj {any}
 * @return {boolean}
 */
export const isJQueryObject = ( obj: any ): boolean => {
	return obj !== undefined && ( obj?.prevObject !== undefined || obj?.jquery !== undefined );
};

/**
 * @description Checks if var is undefined
 * @param value {Object}
 * @return {Boolean} True if `value` is undefined.
 */
export const isUndefined = ( value: any ): boolean => {
	return typeof value === TrueType.UNDEFINED;
};

/**
 * @description Determines if a reference is defined.
 * @param value {Object}
 * @return {Boolean} True if `value` is defined.
 */
export const isDefined = ( value: any ): boolean => {
	return typeof value !== TrueType.UNDEFINED;
};

/**
 * @description Determines if a reference is a Date.
 * @param value {Object}
 * @return {Boolean}
 */
export const isDate = ( value: any ): boolean => {
	return Object.prototype.toString.call( value ) === '[object Date]';
};

/**
 * @description Error
 * @param value {*}
 * @return {*|boolean}
 */
export const isError = ( value: any ): boolean => {
	return value instanceof Error && typeof value?.message !== TrueType.UNDEFINED;
};

/**
 * @description Null
 * @param value {*}
 * @return {*|boolean}
 */
export const isNull = ( value: any ): boolean => {
	return value == null;
};

/**
 * @description Null or undefined
 * @param value {*}
 * @return {*|boolean}
 */
export const isNullOrUndefined = ( value: any ): boolean => {
	return value == null || typeof value === TrueType.UNDEFINED;
};

/**
 * @description Promise
 * @param value {*}
 * @return {*|boolean}
 */
export const isPromise = ( value: any ): boolean => {
	return !!value && ( typeof value === TrueType.OBJECT || typeof value === TrueType.FUNCTION ) && typeof value.then === TrueType.FUNCTION;
};

/**
 * @description Regular expression
 * @param value {*}
 * @return {*|boolean}
 */
export const isRegEx = ( value: any ): boolean => {
	return value && typeof value === TrueType.OBJECT && value.constructor === RegExp;
};

/**
 * @description Symbol
 * @param value {*}
 * @return {*|boolean}
 */
export const isSymbol = ( value: any ): boolean => {
	return typeof value === 'symbol';
};

/**
 * @description Checks is string is base64 encoded
 * @see {@link https://stackoverflow.com/questions/8571501/how-to-check-whether-a-string-is-base64-encoded-or-not}
 * @param str {String}
 * @return {Boolean}
 */
export const isBase64 = ( str: string ): boolean => {
	if ( isString( str ) === false ) return false; // sanity check

	const regex: RegExp = new RegExp( '^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{4}|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)$' );

	if ( str.indexOf( ' ' ) !== -1 ) { // string contains spaces
		return false; // not base64
	} else {
		return ( str.match( regex ) !== null );
	}
};

/**
 * @description Checks if object is XML document
 * @param element
 * @return {boolean}
 */
export const isXML = ( element ): boolean => {
	// documentElement is verified for cases where it doesn't yet exist
	// (such as loading iframes in IE - #4833)
	const documentElement = ( element ? element.ownerDocument || element : 0 ).documentElement;

	return documentElement ? documentElement.nodeName !== 'HTML' : false;
};